import { useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { adminGetConvenienceFees, adminGetPaymentMethodFees, adminSaveConvenienceFees, adminSavePaymentMethodFees } from '../../api/pricing';
import { formatINR } from '../../shared/data/common';
import AdminShell, { useAdminAccess } from './AdminShell';

const toRow = (fee) => ({ ...fee, amount: String(fee.amount ?? ''), maxAmount: fee.maxAmount == null ? '' : String(fee.maxAmount) });

// What a customer would pay on a sample ₹10,000 booking for 2 travellers.
function example(row) {
  const amount = Number(row.amount) || 0;
  if (!row.isActive || amount <= 0) return 'No fee';
  if (row.feeType === 'Percentage') {
    const fee = Math.round((10000 * amount) / 100);
    const max = Number(row.maxAmount) || Infinity;
    return `${formatINR(Math.min(fee, max))} on a ₹10,000 booking`;
  }
  return row.perTraveller ? `${formatINR(amount * 2)} for 2 travellers` : `${formatINR(amount)} per booking`;
}

const methodLabels = {
  Upi: 'UPI',
  DebitCard: 'Debit card',
  CreditCard: 'Credit card',
  NetBanking: 'Net banking',
  InternationalCard: 'International card',
};

// What a customer would pay on a sample ₹10,000 payment.
function methodExample(row) {
  const amount = Number(row.amount) || 0;
  if (!row.isActive || amount <= 0) return 'No fee';
  if (row.feeType === 'Percentage') {
    const max = Number(row.maxAmount) || Infinity;
    return `${formatINR(Math.min(Math.round((10000 * amount) / 100), max))} on a ₹10,000 payment`;
  }
  return `${formatINR(amount)} per booking`;
}

/**
 * Transaction fee per payment method. Customers don't see it separately: it is added to the product's
 * convenience fee for the method they choose.
 */
function PaymentMethodFees({ token }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    adminGetPaymentMethodFees(token)
      .then((fees) => active && setRows(fees.map(toRow)))
      .catch((requestError) => active && setError(requestError.message));
    return () => { active = false; };
  }, [token]);

  const update = (method, field) => (event) => {
    const { type, checked, value } = event.target;
    setMessage('');
    setRows((list) => list.map((r) => (r.method === method ? { ...r, [field]: type === 'checkbox' ? checked : value } : r)));
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await adminSavePaymentMethodFees(token, rows.map((r) => ({
        method: r.method,
        isActive: r.isActive,
        feeType: r.feeType,
        amount: Number(r.amount) || 0,
        maxAmount: r.feeType === 'Percentage' && r.maxAmount !== '' ? Number(r.maxAmount) : null,
      })));
      setRows(saved.map(toRow));
      setMessage('Transaction fees saved. Customers see them included in the convenience fee for the payment method they choose.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ marginTop: 32 }}>
      <h2>Transaction fees by payment method</h2>
      <p className="muted small">
        Added to the product's convenience fee when the customer pays with that method; the customer sees one convenience fee
        (markup + transaction fee). A percentage is of the amount paid (fare + convenience fee − discount).
      </p>
      {error && <p className="api-message error" role="alert">{error}</p>}
      {message && <p className="api-message success" role="status">{message}</p>}
      {!rows && !error && <p role="status">Loading transaction fees…</p>}
      {rows && (
        <form onSubmit={save}>
          <div className="admin-table-wrap">
            <table className="admin-table fee-table">
              <thead><tr><th>Payment method</th><th>Charge fee</th><th>Fee type</th><th>Amount</th><th>Maximum</th><th>Example</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const label = methodLabels[r.method] || r.method;
                  const isFlat = r.feeType === 'Flat';
                  return (
                    <tr key={r.method}>
                      <td><b>{label}</b></td>
                      <td><input type="checkbox" aria-label={`Charge a transaction fee on ${label}`} checked={r.isActive} onChange={update(r.method, 'isActive')} /></td>
                      <td>
                        <select aria-label={`${label} fee type`} value={r.feeType} onChange={update(r.method, 'feeType')} disabled={!r.isActive}>
                          <option value="Flat">Flat ₹</option>
                          <option value="Percentage">% of payment</option>
                        </select>
                      </td>
                      <td>
                        <input type="number" aria-label={`${label} fee amount`} min="0" max={isFlat ? 100000 : 100} step="any"
                          required={r.isActive} disabled={!r.isActive} value={r.amount} onChange={update(r.method, 'amount')} />
                      </td>
                      <td>
                        {!isFlat && (
                          <input type="number" aria-label={`${label} maximum fee`} min="1" step="1" placeholder="Max ₹ (optional)"
                            disabled={!r.isActive} value={r.maxAmount} onChange={update(r.method, 'maxAmount')} />
                        )}
                      </td>
                      <td className="fee-example">{methodExample(r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="admin-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save transaction fees'}</button>
          </div>
        </form>
      )}
    </section>
  );
}

export default function AdminFeesPage() {
  const { auth } = useAuth();
  const hasAccess = useAdminAccess();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!hasAccess) return undefined;
    let active = true;
    adminGetConvenienceFees(auth.accessToken)
      .then((fees) => active && setRows(fees.map(toRow)))
      .catch((requestError) => active && setError(requestError.message));
    return () => { active = false; };
  }, [hasAccess, auth?.accessToken]);

  const update = (product, field) => (event) => {
    const { type, checked, value } = event.target;
    setMessage('');
    setRows((list) => list.map((r) => (r.product === product ? { ...r, [field]: type === 'checkbox' ? checked : value } : r)));
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await adminSaveConvenienceFees(auth.accessToken, rows.map((r) => ({
        product: r.product,
        isActive: r.isActive,
        feeType: r.feeType,
        amount: Number(r.amount) || 0,
        perTraveller: r.feeType === 'Flat' && r.perTraveller,
        maxAmount: r.feeType === 'Percentage' && r.maxAmount !== '' ? Number(r.maxAmount) : null,
      })));
      setRows(saved.map(toRow));
      setMessage('Convenience fees saved. New bookings show them on the review page before payment.');
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminShell title="Convenience fees" subtitle="Charged on each booking and shown to the customer in the fare summary before payment.">
      {error && <p className="api-message error" role="alert">{error}</p>}
      {message && <p className="api-message success" role="status">{message}</p>}
      {!rows && !error && <p role="status">Loading fees…</p>}
      {rows && (
        <form onSubmit={save}>
          <div className="admin-table-wrap">
            <table className="admin-table fee-table">
              <thead><tr><th>Product</th><th>Charge fee</th><th>Fee type</th><th>Amount</th><th>Applies</th><th>Example</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const isFlat = r.feeType === 'Flat';
                  return (
                    <tr key={r.product}>
                      <td><b>{r.product}</b></td>
                      <td><input type="checkbox" aria-label={`Charge a fee on ${r.product}`} checked={r.isActive} onChange={update(r.product, 'isActive')} /></td>
                      <td>
                        <select aria-label={`${r.product} fee type`} value={r.feeType} onChange={update(r.product, 'feeType')} disabled={!r.isActive}>
                          <option value="Flat">Flat ₹</option>
                          <option value="Percentage">% of booking</option>
                        </select>
                      </td>
                      <td>
                        <input type="number" aria-label={`${r.product} fee amount`} min="0" max={isFlat ? 100000 : 100} step="any"
                          required={r.isActive} disabled={!r.isActive} value={r.amount} onChange={update(r.product, 'amount')} />
                      </td>
                      <td>
                        {isFlat ? (
                          <label className="check">
                            <input type="checkbox" checked={r.perTraveller} disabled={!r.isActive} onChange={update(r.product, 'perTraveller')} />
                            <span>Per traveller</span>
                          </label>
                        ) : (
                          <input type="number" aria-label={`${r.product} maximum fee`} min="1" step="1" placeholder="Max ₹ (optional)"
                            disabled={!r.isActive} value={r.maxAmount} onChange={update(r.product, 'maxAmount')} />
                        )}
                      </td>
                      <td className="fee-example">{example(r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="admin-actions" style={{ marginTop: 16 }}>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save fees'}</button>
          </div>
        </form>
      )}
      {hasAccess && <PaymentMethodFees token={auth.accessToken} />}
    </AdminShell>
  );
}
